import React from 'react'
import {graphql} from 'gatsby'
import Img from 'gatsby-image'
import {transparentize, readableColor} from 'polished'
import styled from 'styled-components'
import {config, useSpring, animated} from 'react-spring'
import Layout from '../components/layout'
import {Box, AnimatedBox, Button} from '../elements'
import SEO from '../components/SEO'
import {Helmet} from 'react-helmet'

const PBox = styled(AnimatedBox)`
  max-width: 1400px;
  margin: 0 auto;
`

const Content = styled(Box)<{bg: string}>`
  background-color: ${(props) => transparentize(0.9, props.bg)};

  .gatsby-image-wrapper:not(:last-child) {
    margin-bottom: ${(props) => props.theme.space[10]};

    @media (max-width: ${(props) => props.theme.breakpoints[3]}) {
      margin-bottom: ${(props) => props.theme.space[8]};
    }
  }
`

const Category = styled(AnimatedBox)`
  letter-spacing: 0.05em;
  font-size: ${(props) => props.theme.fontSizes[1]};
  text-transform: uppercase;
`

const Description = styled(animated.div)`
  max-width: 960px;
  letter-spacing: -0.003em;
  --baseline-multiplier: 0.179;
  --x-height-multiplier: 0.35;
  line-height: 1.58;
`

const PButton = styled(Button)<{color: string}>`
  background: ${(props) => (props.color === 'white' ? 'black' : props.color)};
  color: ${(props) =>
    readableColor(props.color === 'white' ? 'black' : props.color)};
`

type PageProps = {
  data: {
    product: {
      title_detail: string
      color: string
      category: string
      desc: string
      slug: string
      parent: {
        modifiedTime: string
        birthTime: string
      }
      cover: {
        childImageSharp: {
          resize: {
            src: string
          }
        }
      }
    }
    images: {
      nodes: {
        name: string
        childImageSharp: {
          fluid: {
            aspectRatio: number
            src: string
            srcSet: string
            sizes: string
            base64: string
            tracedSVG: string
            srcWebp: string
            srcSetWebp: string
          }
        }
      }[]
    }
  }
}

const Kino: React.FunctionComponent<PageProps> = ({
  data: {product, images},
}) => {
  const categoryAnimation = useSpring({
    config: config.slow,
    from: {opacity: 0, transform: 'translate3d(0, -30px, 0)'},
    to: {opacity: 1, transform: 'translate3d(0, 0, 0)'},
  })

  const titleAnimation = useSpring({
    config: config.slow,
    delay: 300,
    from: {opacity: 0},
    to: {opacity: 1},
  })
  const descAnimation = useSpring({
    config: config.slow,
    delay: 600,
    from: {opacity: 0},
    to: {opacity: 1},
  })
  const imagesAnimation = useSpring({
    config: config.slow,
    delay: 800,
    from: {opacity: 0},
    to: {opacity: 1},
  })

  const openCheckout = () => {
    const Paddle = window.Paddle
    Paddle.Checkout.open({product: 596756})
  }

  const handleChangeClientState = (newState: any, addedTags: any) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(
        ({src}: any) => src === process.env.PADDLE_SCRIPT,
      )
      if (foundScript) {
        foundScript.addEventListener(
          'load',
          () =>
            window.Paddle.Setup({
              vendor: parseInt(process.env.PADDLE_VENDOR_ID),
            }),
          {
            once: true,
          },
        )
      }
    }
  }

  return (
    <Layout color={product.color}>
      <Helmet
        script={[
          {
            src: process.env.PADDLE_SCRIPT,
          },
        ]}
        onChangeClientState={handleChangeClientState}
      />
      <SEO
        pathname={product.slug}
        title={`${product.title_detail}`}
        desc={product.desc}
        node={product.parent}
        individual
      />
      <PBox py={10} px={[6, 6, 8, 10]}>
        <Category style={categoryAnimation}>{product.category}</Category>
        <animated.h1 style={titleAnimation}>
          {product.title_detail} Kino
        </animated.h1>
        <Description style={descAnimation}>
          <div dangerouslySetInnerHTML={{__html: product.desc}} />
        </Description>
      </PBox>
      <PBox style={{textAlign: 'center'}} py={10} px={[6, 6, 8, 10]}>
        <animated.h2
          style={titleAnimation}
        >{`Want your own pink cat with ${product.title_detail} personality?`}</animated.h2>
        <Img fluid={product.cover.childImageSharp.fluid} />
        <PButton color={product.color} py={4} px={8} onClick={openCheckout}>
          Get Kino
        </PButton>
      </PBox>
    </Layout>
  )
}

export default Kino

export const query = graphql`
  query ProductTemplate($slug: String!) {
    product: productsYaml(slug: {eq: $slug}) {
      title_detail
      color
      category
      desc
      slug
      parent {
        ... on File {
          modifiedTime
          birthTime
        }
      }
      cover {
        childImageSharp {
          fluid(maxWidth: 500, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  }
`
